/**
 * PPSR Service
 * Re-validates PPSR when a deal reaches paid status, via a real PPSR Cloud
 * B2B call (Cloudflare Function /api/ppsr/revalidate — the client secret
 * stays server-side, same pattern as the Resend email fix).
 * Framework: PPSR certificate required for all deals (Part F.2/F.5).
 *
 * Provider note (2026-08-17, approved by Winnie): this uses PPSR Cloud, not
 * AutoGrab — AutoGrab remains the real, working provider for the separate
 * deal-creation-time check in src/lib/autograb.ts (checkPPSR), which this
 * file does not touch.
 *
 * Timing note (2026-08-17, Nick's call): this still fires *after* payment
 * (see workflowTriggers.ts TRIGGER 4 / wbcService.ts), same as before — it's
 * a real record now, not a pre-payment gate. See functions/api/ppsr/revalidate.js
 * for the full explanation of that tradeoff.
 *
 * The DB update (deals.ppsr_status/ppsr_discharge_date/ppsr_reference_number/
 * ppsr_details) happens server-side inside the Cloudflare Function, using the
 * service role — this file just calls it and returns the same PPSRResult
 * shape as before, so workflowTriggers.ts needs no changes.
 */

import { apiHeadersOrThrow } from '../lib/authHeader'

export interface PPSRResult {
    success: boolean;
    message: string;
    details?: {
      certificate_path?: string;
      ppsr_reference?: string;
      certificate_url?: string;
    };
}

export const ppsrService = {
  /**
   * Run the PPSR search for a deal, with whatever identifier we hold: a VIN, or a
   * rego and the state that issued it. The Function resolves a plate to a VIN
   * through NEVDIS before it searches, writes the outcome onto the deal itself and
   * files the certificate, so the answer survives whatever the browser does next.
   *
   * This never throws. PPSR being slow, down or unentitled must not be the reason
   * a buyer cannot record a car they have just bought.
   */
  runSearch: async (
    dealId: string,
    identifiers: { vin?: string | null; rego?: string | null; state?: string | null },
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const res = await fetch('/api/ppsr/revalidate', {
        method: 'POST',
        headers: await apiHeadersOrThrow(),
        // keepalive, because the moment a deal is created is exactly when someone
        // closes the tab. Without it the search dies with the page and the deal is
        // left saying it is still checking, forever.
        keepalive: true,
        body: JSON.stringify({
          dealId,
          vin: identifiers.vin || '',
          rego: identifiers.rego || '',
          state: identifiers.state || '',
        }),
      })
      const payload = await res.json().catch(() => ({}))
      return {
        success: res.ok && payload.success !== false,
        message: payload.message,
      }
    } catch (err) {
      console.error('PPSR search could not be started', err)
      return {
        success: false,
        message: err instanceof Error ? err.message : 'The PPSR search could not be started',
      }
    }
  },

    /**
         * Re-validate PPSR via PPSR Cloud.
     * Framework: Compulsory when deal reaches paid status.
     */
    generateDischarge: async (
          dealId: string,
          vin: string,
          _sellerName: string,
          _vehicleDescription: string
        ): Promise<PPSRResult> => {
          if (!vin) {
                  return {
                            success: false,
                            message: 'VIN required for PPSR discharge'
                  };
          }

      // This used to POST to /api/ppsr/revalidate, which re-runs a PPSR
      // *search*. A search is not a discharge: the registration Direct Auto
      // lodged against the vehicle stayed on the national register, publicly
      // visible, after the deal was paid. functions/api/ppsr/discharge.js has
      // always existed and was called from nowhere.
      //
      // Only a deal that actually lodged has something to discharge, so a
      // "nothing was lodged" answer from the endpoint is reported as a
      // no-op rather than as a failure — most deals elect not to lodge.
      try {
              const res = await fetch('/api/ppsr/discharge', {
                        method: 'POST',
                        // Discharge writes to the national register, so the Function needs to know
      // who is asking. Throwing on a missing session keeps an anonymous discharge
      // from ever leaving the browser.
      headers: await apiHeadersOrThrow(),
                        body: JSON.stringify({ dealId }),
              });
              const result: PPSRResult = await res.json().catch(() => ({
                        success: false,
                        message: 'PPSR discharge returned a response that could not be read.',
              }));
              // Not res.ok alone — the body has to say it worked.
              if (!res.ok || result?.success !== true) {
                        return {
                                  success: false,
                                  message: result?.message || `PPSR discharge failed (HTTP ${res.status}).`,
                                  details: (result as any)?.details,
                        };
              }
              return result;
      } catch (err) {
              const errorMsg = err instanceof Error ? err.message : 'Unknown error';
              return {
                        success: false,
                        message: `PPSR discharge request failed: ${errorMsg}`
              };
      }
    }
};
